import {
  Chip,
  column,
  Css,
  defaultPage,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  Tag,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { createDevelopmentContractUrl, createProjectBidContractOverviewUrl } from "src/RouteUrls";
import {
  BidContractsTableV2_BidContractFragment,
  BidContractsTableV2Query,
  useBidContractsTableV2Query,
} from "src/components/bid-contracts/BidContractsTableV2.generated";
import { BidContractsFilter, BidScopeSource } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { bidHierarchyColumn } from "src/routes/bids/BidPackagesPage";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { chipCell, dateCell } from "../gridTableCells";
import { getTagTypeFromStatus } from "./BidContractHeader";

export function BidContractsTableV2() {
  const project = useProjectContext();
  const { projectId } = useRouteParams<{ projectId: string }>();
  const filterDefs: FilterDefs<BidContractsFilter> = useMemo(
    () => ({
      // If we are viewing project bid packages, include the toggle to show bids from all levels
      ...(project.id
        ? { includeRelatedProjectBids: toggleFilter({ label: "Show Bids from All Levels", defaultValue: true }) }
        : {}),
    }),
    [project],
  );
  const layoutState = useGridTableLayoutState<BidContractsFilter>({
    persistedFilter: { storageKey: "bidContractsV2Filter", filterDefs },
    search: "server",
  });
  const { includeRelatedProjectBids } = layoutState.filter;
  const query = useBidContractsTableV2Query({
    variables: {
      filter: {
        search: layoutState.searchString ?? "",
        // If on a project route, load only the bid contracts for that project
        ...(projectId && { projects: [projectId] }),
        // If we are viewing subdivision bids, include child project bids toggle determines if Lot bids are included
        ...(project.__typename === "Subdivision"
          ? {
              scopeSource: includeRelatedProjectBids
                ? [BidScopeSource.Subdivision, BidScopeSource.Lot]
                : [BidScopeSource.Subdivision],
            }
          : { includeRelatedProjectBids }),
      },
      page: defaultPage,
    },
  });

  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.bidContractsPage?.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.bidContractsPage.entities.length, limit: defaultPage.limit } },
      });
    }
  }, [query]);

  const columns = useMemo(() => createColumns(), []);
  const rowStyles: RowStyles<Row> = {
    data: {
      rowLink: (row) =>
        row.data.developments.nonEmpty
          ? createDevelopmentContractUrl(row.data.developments[0].id, row.data.latestRevision.id)
          : createProjectBidContractOverviewUrl(
              projectId ?? row.data.latestRevision.bidContract.projects[0].id,
              row.data.latestRevision.id,
            ),
    },
  };

  return (
    <GridTableLayout
      layoutState={layoutState}
      hideEditColumns
      tableProps={{
        query,
        createRows,
        columns,
        rowStyles,
        as: "virtual",
        emptyFallback: "There aren't any bid contracts to display.",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

type Row = SimpleHeaderAndData<BidContractsTableV2_BidContractFragment>;

function createRows(data: BidContractsTableV2Query | undefined): GridDataRow<Row>[] {
  return simpleDataRows(data?.bidContractsPage?.entities);
}

const createColumns = (): GridColumn<Row>[] => [
  column<Row>({ header: "Trade Partner", data: ({ displayName }) => displayName, w: 2, mw: "200px" }),
  column<Row>({
    header: "Version",
    data: (bc) => ({ content: bc.latestRevision.version, value: parseInt(bc.latestRevision.version || "0") }),
    mw: "80px",
  }),
  column<Row>({
    header: "Cost Codes",
    data: ({ latestRevision }) => {
      const hasAward = latestRevision.costCodeAwards.nonEmpty;
      if (!hasAward) return chipCell(latestRevision.costCodes.map((cc) => cc.number));
      return (
        <div css={Css.df.gapPx(5).fww.$}>
          {latestRevision.costCodeAwards.map((c) => {
            const award = c.isPrimary ? "Primary" : c.isSecondary ? "Secondary" : "None";
            return (
              <div css={Css.df.fdr.gap1.aic.$} key={c.costCode.id}>
                <Chip text={c.costCode.number} xss={Css.mt0.$} />
                {award}
              </div>
            );
          })}
        </div>
      );
    },
    mw: "160px",
  }),
  column<Row>({
    header: "Hierarchy",
    data: ({ projects, scopeSource }) => bidHierarchyColumn({ projects, scopeSource }),
    mw: "120px",
  }),
  column<Row>({
    header: () => "Status",
    data: ({ latestRevision }) => ({
      content: (
        <>
          <Tag text={latestRevision.status?.name || "Draft"} type={getTagTypeFromStatus(latestRevision.status?.code)} />
        </>
      ),
      value: latestRevision.status?.code,
    }),
    mw: "100px",
  }),
  column<Row>({
    header: "Effective On",
    data: ({ latestRevision }) => dateCell(latestRevision.startDate, { dateFormatStyle: "long" }),
    mw: "140px",
  }),
  column<Row>({
    header: "Expires On",
    data: ({ latestRevision }) => dateCell(latestRevision.endDate, { dateFormatStyle: "long" }),
    mw: "140px",
  }),
];
